import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth, api, COUNTRIES } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Briefcase, User, FileText, Wrench, Star, Loader2, ChevronRight, 
  Globe, MapPin, Download, Sparkles, Settings, Clock, Bell, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { NotificationPreferences } from '@/components/NotificationPreferences';

// Worker Dashboard Navigation
const WorkerNav = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/worker/dashboard" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.dashboard')}
            </Link>
            <Link to="/worker/capability-card" className="text-muted-foreground hover:text-foreground transition">
              {t('capabilityCard.viewCard')}
            </Link>
            <Link to="/worker/applications" className="text-muted-foreground hover:text-foreground transition">
              {t('jobs.applications')}
            </Link>
            <Link to="/worker/notifications" className="text-muted-foreground hover:text-foreground transition flex items-center gap-1">
              <Bell className="h-4 w-4" />
              {t('nav.alerts')}
            </Link>
            <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.browse')} {t('nav.jobs')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <span className="text-sm text-muted-foreground">{user?.nickname}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Worker Dashboard
export const WorkerDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [capabilityCard, setCapabilityCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'worker') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [appsRes] = await Promise.all([
        api.getMyApplications(),
      ]);
      setApplications(appsRes.data);
      
      try {
        const cardRes = await api.getCapabilityCard();
        setCapabilityCard(cardRes.data);
      } catch {
        // No card yet
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <WorkerNav />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-1/3 mb-8" />
          <div className="grid md:grid-cols-3 gap-6">
            {[1,2,3].map(i => <Skeleton key={i} className="h-48" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <WorkerNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="worker-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t('workerDashboard.welcome', { name: user?.nickname })}</h1>
            <p className="text-muted-foreground">{t('workerDashboard.subtitle')}</p>
          </div>
          <Link to="/jobs">
            <Button data-testid="browse-jobs-btn">
              <Briefcase className="mr-2 h-4 w-4" />
              {t('landing.browseJobs')}
            </Button>
          </Link>
        </div>

        {/* Profile Completion Banner */}
        {!capabilityCard && (
          <Card className="mb-8 border-brand bg-brand/5" data-testid="complete-profile-banner">
            <CardContent className="flex justify-between items-center py-4">
              <div className="flex items-center gap-4">
                <User className="h-8 w-8 text-brand" />
                <div>
                  <p className="font-semibold">{t('workerDashboard.completeProfile')}</p>
                  <p className="text-sm text-muted-foreground">{t('workerDashboard.completeProfileDesc')}</p>
                </div>
              </div>
              <Link to="/worker/capability-card">
                <Button data-testid="create-card-btn">
                  {t('capabilityCard.createCard')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stat-applications">
            <CardHeader className="pb-2">
              <CardDescription>{t('workerDashboard.applications')}</CardDescription>
              <CardTitle className="text-3xl">{applications.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-pending">
            <CardHeader className="pb-2">
              <CardDescription>{t('workerDashboard.pending')}</CardDescription>
              <CardTitle className="text-3xl">{applications.filter(a => a.status === 'pending').length}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-profile">
            <CardHeader className="pb-2">
              <CardDescription>{t('workerDashboard.profileStatus')}</CardDescription>
              <CardTitle className="text-3xl">{capabilityCard ? t('workerDashboard.complete') : t('workerDashboard.incomplete')}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-cv">
            <CardHeader className="pb-2">
              <CardDescription>{t('workerDashboard.aiCV')}</CardDescription>
              <CardTitle className="text-3xl">{capabilityCard?.generated_cv ? t('workerDashboard.generated') : t('workerDashboard.notYet')}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="card-hover">
            <CardHeader>
              <FileText className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('capabilityCard.title')}</CardTitle>
              <CardDescription>{t('capabilityCard.subtitle')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/worker/capability-card" className="w-full">
                <Button variant="outline" className="w-full" data-testid="view-card-btn">
                  {capabilityCard ? t('capabilityCard.viewCard') : t('capabilityCard.createCard')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <Briefcase className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('workerDashboard.myApplications')}</CardTitle>
              <CardDescription>{t('workerDashboard.trackApplications')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/worker/applications" className="w-full">
                <Button variant="outline" className="w-full" data-testid="view-applications-btn">
                  {t('workerDashboard.viewApplications')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <Wrench className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('workerDashboard.toolRentals')}</CardTitle>
              <CardDescription>{t('workerDashboard.rentOutTools')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/tools" className="w-full">
                <Button variant="outline" className="w-full" data-testid="tools-btn">
                  {t('workerDashboard.browseTools')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

        </div>

        {/* Job Alerts Section with NotificationPreferences Component */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">{t('workerDashboard.jobAlerts')}</h2>
          <NotificationPreferences />
        </div>

        {/* Recent Applications */}
        {applications.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">{t('workerDashboard.recentApplications')}</h2>
            <div className="space-y-4">
              {applications.slice(0, 3).map((app) => (
                <Card key={app.id}>
                  <CardContent className="flex justify-between items-center py-4">
                    <div>
                      <p className="font-semibold">Application #{app.id.slice(0, 8)}</p>
                      <p className="text-sm text-muted-foreground">
                        Status: <Badge variant="outline" className="ml-1 capitalize">{app.status}</Badge>
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(app.created_at).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Work Capability Card Page
export const CapabilityCardPage = () => {
  const { user } = useAuth();
  const { t, availableLanguages, languageNames } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingCV, setGeneratingCV] = useState(false);
  const [card, setCard] = useState(null);
  const [formData, setFormData] = useState({
    skills: [],
    immediate_availability: '',
    tools_vehicle: '',
    availability_notes: '',
    preferred_countries: [],
    preferred_regions: [],
    languages: []
  });
  const [newSkill, setNewSkill] = useState('');
  const [newRegion, setNewRegion] = useState('');
  const [newLanguage, setNewLanguage] = useState('');

  useEffect(() => {
    if (user?.role !== 'worker') {
      navigate('/');
      return;
    }
    fetchCard();
  }, [user]);

  const fetchCard = async () => {
    try {
      const res = await api.getCapabilityCard();
      setCard(res.data);
      setFormData({
        skills: res.data.skills || [],
        immediate_availability: res.data.immediate_availability,
        tools_vehicle: res.data.tools_vehicle,
        availability_notes: res.data.availability_notes,
        preferred_countries: res.data.preferred_countries || [],
        preferred_regions: res.data.preferred_regions || [],
        languages: res.data.languages || []
      });
    } catch {
      // No card yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (formData.skills.length === 0) {
      toast.error('Please add at least one skill');
      return;
    }
    if (!formData.immediate_availability) {
      toast.error('Please specify your availability');
      return;
    }

    setSaving(true);
    try {
      await api.createCapabilityCard(formData);
      toast.success('Capability Card saved!');
      fetchCard();
    } catch (error) {
      toast.error(error.response?.data?.detail);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCV = async () => {
    setGeneratingCV(true);
    try {
      const res = await api.generateCV();
      toast.success('CV generated successfully!');
      setCard({...card, generated_cv: res.data.cv});
    } catch (error) {
      toast.error(error.response?.data?.detail);
    } finally {
      setGeneratingCV(false);
    }
  };

  const addSkill = () => {
    if (newSkill && !formData.skills.includes(newSkill)) {
      setFormData({...formData, skills: [...formData.skills, newSkill]});
      setNewSkill('');
    }
  };

  const addCountry = (code) => {
    if (code && !formData.preferred_countries.includes(code)) {
      setFormData({...formData, preferred_countries: [...formData.preferred_countries, code]});
    }
  };

  const addRegion = () => {
    if (newRegion && !formData.preferred_regions.includes(newRegion)) {
      setFormData({...formData, preferred_regions: [...formData.preferred_regions, newRegion]});
      setNewRegion('');
    }
  };

  const addLanguage = () => {
    if (newLanguage && !formData.languages.includes(newLanguage)) {
      setFormData({...formData, languages: [...formData.languages, newLanguage]});
      setNewLanguage('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <WorkerNav />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-1/2 mb-8" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <WorkerNav />
      
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="capability-card-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{t('microcopy.megaMicrocopy.cv.header')}</h1>
          <p className="text-muted-foreground">{t('microcopy.megaMicrocopy.cv.helper')}</p>
        </div>
        
        {/* Motivational intro banner */}
        <div className="bg-brand/5 border border-brand/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-brand/10 rounded-lg">
              <Sparkles className="h-5 w-5 text-brand" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">{t('microcopy.megaMicrocopy.cv.motivationSkills')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('microcopy.megaMicrocopy.cv.motivationProgress')}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                  {t('microcopy.cvBuilder.dontWorrySpelling')}
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                  {t('microcopy.cvBuilder.translateAnywhere')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="edit">
          <TabsList className="mb-6">
            <TabsTrigger value="edit">{t('cvBuilder.editTab')}</TabsTrigger>
            <TabsTrigger value="preview">{t('cvBuilder.previewTab')}</TabsTrigger>
            {card?.generated_cv && <TabsTrigger value="cv">{t('cvBuilder.aiCvTab')}</TabsTrigger>}
          </TabsList>

          <TabsContent value="edit">
            <Card>
              <CardContent className="pt-6 space-y-6">
                {/* Skills */}
                <div className="space-y-2">
                  <Label>{t('cvBuilder.skillsRequired')}</Label>
                  <p className="text-sm text-muted-foreground">{t('microcopy.cvBuilder.skills.helper')}</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('cvBuilder.addSkillPlaceholder')}
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                      data-testid="skill-input"
                    />
                    <Button type="button" onClick={addSkill}>{t('cvBuilder.addButton')}</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.skills.map((skill) => (
                      <Badge key={skill} variant="secondary" className="cursor-pointer"
                        onClick={() => setFormData({...formData, skills: formData.skills.filter(s => s !== skill)})}>
                        {skill} ×
                      </Badge>
                    ))}
                  </div>
                  {formData.skills.length === 0 ? (
                    <p className="text-xs text-brand">{t('microcopy.cvBuilder.skills.encouragement')}</p>
                  ) : formData.skills.length < 3 ? (
                    <p className="text-xs text-amber-600">{t('microcopy.cvBuilder.progress.greatStart')}</p>
                  ) : (
                    <p className="text-xs text-green-600">{t('microcopy.cvBuilder.progress.lookingGood')}</p>
                  )}
                </div>

                {/* Immediate Availability */}
                <div className="space-y-2">
                  <Label>{t('cvBuilder.immediateRequired')}</Label>
                  <p className="text-sm text-muted-foreground">{t('microcopy.cvBuilder.experience.helper')}</p>
                  <p className="text-xs text-muted-foreground">{t('microcopy.cvBuilder.smallExperienceMatters')}</p>
                  <Textarea
                    placeholder={t('cvBuilder.immediatePlaceholder')}
                    value={formData.immediate_availability}
                    onChange={(e) => setFormData({...formData, immediate_availability: e.target.value})}
                    rows={3}
                    data-testid="availability-input"
                  />
                </div>

                {/* Tools/Vehicle */}
                <div className="space-y-2">
                  <Label>{t('cvBuilder.toolsLabel')}</Label>
                  <p className="text-xs text-muted-foreground">{t('microcopy.cvBuilder.tools.helper')}</p>
                  <Input
                    placeholder={t('cvBuilder.toolsPlaceholder')}
                    value={formData.tools_vehicle}
                    onChange={(e) => setFormData({...formData, tools_vehicle: e.target.value})}
                    data-testid="tools-input"
                  />
                </div>

                {/* Availability Notes */}
                <div className="space-y-2">
                  <Label>{t('cvBuilder.availabilityLabel')}</Label>
                  <p className="text-xs text-muted-foreground">{t('microcopy.cvBuilder.availability.helper')}</p>
                  <Textarea
                    placeholder={t('cvBuilder.availabilityPlaceholder')}
                    value={formData.availability_notes}
                    onChange={(e) => setFormData({...formData, availability_notes: e.target.value})}
                    rows={2}
                    data-testid="notes-input"
                  />
                </div>

                {/* Preferred Countries */}
                <div className="space-y-2">
                  <Label>{t('cvBuilder.countriesLabel')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {COUNTRIES.map((country) => (
                      <Badge
                        key={country.code}
                        variant={formData.preferred_countries.includes(country.code) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          if (formData.preferred_countries.includes(country.code)) {
                            setFormData({...formData, preferred_countries: formData.preferred_countries.filter(c => c !== country.code)});
                          } else {
                            addCountry(country.code);
                          }
                        }}
                        data-testid={`country-${country.code}`}
                      >
                        {(() => {
                          const key = `location.countries.${country.code}`;
                          const translated = t(key);
                          return translated !== key ? translated : country.name;
                        })()}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Preferred Regions */}
                <div className="space-y-2">
                  <Label>{t('cvBuilder.regionsLabel')}</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('cvBuilder.regionPlaceholder')}
                      value={newRegion}
                      onChange={(e) => setNewRegion(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRegion())}
                      data-testid="region-input"
                    />
                    <Button type="button" onClick={addRegion}>{t('cvBuilder.addButton')}</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.preferred_regions.map((region) => (
                      <Badge key={region} variant="secondary" className="cursor-pointer"
                        onClick={() => setFormData({...formData, preferred_regions: formData.preferred_regions.filter(r => r !== region)})}>
                        {region} ×
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Languages */}
                <div className="space-y-2">
                  <Label>{t('cvBuilder.languagesLabel')}</Label>
                  <p className="text-sm text-muted-foreground">{t('cvBuilder.languagesHelper')}</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('cvBuilder.languagePlaceholder')}
                      value={newLanguage}
                      onChange={(e) => setNewLanguage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                      data-testid="language-input"
                    />
                    <Button type="button" onClick={addLanguage}>{t('cvBuilder.addButton')}</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.languages.map((lang) => (
                      <Badge key={lang} variant="secondary" className="cursor-pointer"
                        onClick={() => setFormData({...formData, languages: formData.languages.filter(l => l !== lang)})}>
                        {lang} ×
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-card-btn">
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('cvBuilder.saveCard')}
                  </Button>
                  {card && (
                    <Button onClick={handleGenerateCV} disabled={generatingCV} variant="outline" data-testid="generate-cv-btn">
                      {generatingCV && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t('cvBuilder.generateAiCv')}
                    </Button>
                  )}
                </div>
                
                {/* AI CV Features Info */}
                <div className="bg-muted/50 rounded-lg p-4 mt-4">
                  <p className="text-sm font-medium mb-2">{t('cvBuilder.aiCv.features')}</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• {t('cvBuilder.aiCv.fixesSpelling')}</li>
                    <li>• {t('cvBuilder.aiCv.organizes')}</li>
                    <li>• {t('cvBuilder.aiCv.formatting')}</li>
                    <li>• {t('cvBuilder.aiCv.translates')}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preview">
            <Card data-testid="card-preview">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-full bg-brand/10 flex items-center justify-center">
                    <User className="h-8 w-8 text-brand" />
                  </div>
                  <div>
                    <CardTitle>{user?.nickname}</CardTitle>
                    <CardDescription>{t('cvBuilder.preview.title')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">{t('cvBuilder.preview.skillsSection')}</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <Badge key={skill}>{skill}</Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">{t('cvBuilder.preview.canDoSection')}</p>
                  <p>{formData.immediate_availability}</p>
                </div>

                {formData.tools_vehicle && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">{t('cvBuilder.preview.toolsSection')}</p>
                    <p>{formData.tools_vehicle}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">{t('cvBuilder.preview.countriesSection')}</p>
                    <div className="flex flex-wrap gap-1">
                      {formData.preferred_countries.map((code) => {
                        const key = `location.countries.${code}`;
                        const translated = t(key);
                        const name = translated !== key ? translated : (COUNTRIES.find(c => c.code === code)?.name || code);
                        return <Badge key={code} variant="outline">{name}</Badge>;
                      })}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">{t('cvBuilder.preview.languagesSection')}</p>
                    <div className="flex flex-wrap gap-1">
                      {formData.languages.map((lang) => (
                        <Badge key={lang} variant="outline">{lang}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {card?.generated_cv && (
            <TabsContent value="cv">
              <Card data-testid="generated-cv">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-brand" />
                    {t('cvBuilder.aiCv.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap">
                    {card.generated_cv}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" onClick={() => {
                    const blob = new Blob([card.generated_cv], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'cv.txt';
                    a.click();
                  }} data-testid="download-cv-btn">
                    <Download className="mr-2 h-4 w-4" />
                    {t('cvBuilder.downloadCv')}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

// Worker Applications Page
export const WorkerApplicationsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'worker') {
      navigate('/');
      return;
    }
    fetchApplications();
  }, [user]);

  const fetchApplications = async () => {
    try {
      const res = await api.getMyApplications();
      setApplications(res.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <WorkerNav />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-1/2 mb-8" />
          <div className="space-y-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <WorkerNav />
      
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="worker-applications-page">
        <h1 className="text-3xl font-bold mb-8">{t('workerApplications.title')}</h1>

        {applications.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl font-semibold mb-2">{t('workerApplications.noApplications')}</p>
              <p className="text-muted-foreground mb-4">{t('workerApplications.startApplying')}</p>
              <Link to="/jobs">
                <Button>{t('workerApplications.browseJobs')}</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <Card key={app.id} data-testid={`application-${app.id}`}>
                <CardContent className="flex justify-between items-center py-4">
                  <div>
                    <p className="font-semibold">{t('workerApplications.jobLabel')}: {app.job_id.slice(0, 8)}...</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="capitalize">{app.status}</Badge>
                      {app.contact_unlocked && (
                        <Badge className="bg-green-500">{t('admin.contactUnlocked')}</Badge>
                      )}
                    </div>
                    {app.cover_letter && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{app.cover_letter}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      {new Date(app.created_at).toLocaleDateString()}
                    </p>
                    <Link to={`/jobs/${app.job_id}`}>
                      <Button variant="link" size="sm">{t('workerApplications.viewJob')}</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
